import { ApiError } from '../api/client';

/**
 * The ONE user-facing error formatter: every failure an accountant sees is Lithuanian,
 * specific when we can be (the API's ProblemDetails title/detail, known message
 * substrings) and honest-generic when we can't (status fallbacks). Never leak stack
 * traces, vendor names, or raw English server sentences.
 */
export function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = parseProblemDetails(err.body);
    if (detail) {
      const mapped = mapKnownMessage(detail);
      if (mapped) return mapped;
    }
    switch (err.status) {
      case 400: return 'Netinkama užklausa — patikrinkite įvestus duomenis.';
      case 401: return 'Sesija baigėsi — prisijunkite iš naujo.';
      case 403: return 'Neturite teisės šiam veiksmui.';
      case 404: return 'Nerasta — įrašas gali būti perkeltas arba ištrintas.';
      case 409: return 'Konfliktas — duomenys pasikeitė, atnaujinkite ir bandykite dar kartą.';
      default:
        return err.status >= 500
          ? 'Serverio klaida — bandykite dar kartą po akimirkos.'
          : `Klaida (${err.status}).`;
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'Nežinoma klaida.';
}

/** The API's error bodies are ProblemDetails JSON ({"title": …, "detail": …}) or plain text. */
function parseProblemDetails(body: string): string | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === 'object') {
      const detail = (parsed.detail ?? parsed.title ?? parsed.message) as unknown;
      if (typeof detail === 'string' && detail.trim().length > 0) return detail;
    }
  } catch {
    // plain text body
    if (body.trim().length > 0 && body.length < 300) return body.trim();
  }
  return null;
}

/** Known server-message fragments mapped to accountant language (LT, outcome-first). */
function mapKnownMessage(message: string): string | null {
  const m = message.toLowerCase();
  if (m.includes('period is closed') || m.includes('closed period') || m.includes('uždaryt'))
    return 'Periodas uždarytas — šiai datai įrašų keisti negalima.';
  if (m.includes('balance') && (m.includes('invariant') || m.includes('debit') || m.includes('credit')))
    return 'Įrašas nesuderintas — debetas turi lygti kreditui.';
  if (m.includes('workspace') && m.includes('not found'))
    return 'Darbo sritis nerasta — pereikite į kitą įmonę ir bandykite dar kartą.';
  if (m.includes('posted') && (m.includes('immutable') || m.includes('cannot')))
    return 'Užregistruotas įrašas nekeičiamas.';
  if (m.includes('future date'))
    return 'Data ateityje neleidžiama.';
  if (m.includes('validation') || m.includes('is required') || m.includes('must be'))
    return 'Netinkami duomenys — patikrinkite įvestį.';
  return null;
}
